import { Check } from "lucide-react";
import type { JSX } from "react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useRecordingConsentStore } from "@/store/recordingConsent.store";

// "Patient consented to recording": a v3.2 chip-style checkbox in the
// recorder and the submit sheet (VITE_RECORDING_CONSENT_ENABLED). Ticked,
// the visit's note starts with a short consent line.
export const RecordingConsentCheck = ({
  className,
}: {
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const id = useId();
  const consented = useRecordingConsentStore((state) => state.consented);
  const setConsented = useRecordingConsentStore((state) => state.setConsented);
  return (
    <div className={cn("flex flex-col items-start gap-1", className)}>
      <label
        className={cn(
          "inline-flex min-h-11 cursor-pointer select-none items-center gap-2.5 rounded-full py-1.5 pl-2 pr-4 font-v3 text-[14px] font-medium transition-colors focus-within:ring-2 focus-within:ring-v3-brand-ring",
          consented
            ? "bg-v3-ok-tint text-v3-ok-text shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--v3-ok)_35%,transparent)]"
            : "bg-v3-surface-2 text-v3-text-2 shadow-v3-hairline-strong hover:bg-v3-surface-3",
        )}
        data-testid="recording-consent"
        htmlFor={id}
      >
        <input
          checked={consented}
          className="peer sr-only"
          id={id}
          type="checkbox"
          onChange={(event) => {
            setConsented(event.target.checked);
          }}
        />
        <span
          aria-hidden
          className={cn(
            "flex size-5 shrink-0 items-center justify-center rounded-md transition-colors",
            consented
              ? "bg-v3-ok text-white"
              : "bg-v3-panel shadow-v3-hairline-strong",
          )}
        >
          {consented && <Check className="size-3.5" strokeWidth={3} />}
        </span>
        {t("visit.consent.label")}
      </label>
      <p className="px-2 text-[12.5px] leading-[18px] text-v3-text-3">
        {consented ? t("visit.consent.onHint") : t("visit.consent.offHint")}
      </p>
    </div>
  );
};
